import {
  getEquivalentRevitParameterNames,
  sanitizeRevitParameterNames,
} from '../revitParameterNames';

export type ParameterBindingKind = 'type' | 'instance';

export interface ParameterTypeHint {
  name: string;
  dataType: string;
}

export interface AvailableProjectParameterDescriptor {
  name: string;
  label?: string;
  parameterType: 'Type' | 'Instance';
  group?: string;
  categories?: string[];
  dataType?: string;
  isProjectBinding?: boolean;
  /** Where the parameter definition was created (shared/project/family/built-in). */
  source?: import('../../api/grpc/gen/family_service_pb').ParameterSource;
}

export interface ProjectParameterMutationBatch {
  bindingKind: ParameterBindingKind;
  parameterNames: string[];
}

function normalizeProjectParameterCategoryKey(category: string): string {
  const normalized = category.trim().toLowerCase();
  const compact = normalized.replace(/\s+/g, '');

  // The host accepts these legacy spellings as aliases for one Revit
  // BuiltInCategory. Catalog responses contain only Revit's display name, so
  // comparing the raw request strings would make an already-complete binding
  // look perpetually incomplete.
  if (compact === 'flexpipes' || compact === 'flexpipecurves') {
    return 'flex pipes';
  }

  if (compact === 'pipingsystem' || compact === 'pipingsystems') {
    return 'piping systems';
  }

  return normalized;
}

function normalizeProjectParameterCategoryLabel(category: string): string {
  const key = normalizeProjectParameterCategoryKey(category);
  if (key === 'flex pipes') {
    return 'Flex Pipes';
  }

  if (key === 'piping systems') {
    return 'Piping Systems';
  }

  return category.trim();
}

export function toProjectParameterType(
  bindingKind: ParameterBindingKind
): 'Type' | 'Instance' {
  return bindingKind === 'type' ? 'Type' : 'Instance';
}

export function normalizeProjectParameterCategories(categories: unknown): string[] {
  if (!Array.isArray(categories)) {
    return [];
  }

  const normalized = new Map<string, string>();
  categories
    .filter((category): category is string => typeof category === 'string')
    .map((category) => category.trim())
    .filter(Boolean)
    .forEach((category) => {
      const key = normalizeProjectParameterCategoryKey(category);
      if (!normalized.has(key)) {
        normalized.set(key, normalizeProjectParameterCategoryLabel(category));
      }
    });

  return Array.from(normalized.values());
}

function descriptorCoversCategories(
  parameter: AvailableProjectParameterDescriptor,
  requestedCategories: string[]
): boolean {
  const normalizedRequestedCategories = normalizeProjectParameterCategories(requestedCategories);
  if (normalizedRequestedCategories.length === 0) {
    return true;
  }

  const availableCategories = normalizeProjectParameterCategories(parameter.categories);
  if (availableCategories.length === 0) {
    return parameter.group?.trim().toLowerCase() === 'project information' &&
      normalizedRequestedCategories.every(
        (category) => normalizeProjectParameterCategoryKey(category) === 'project information'
      );
  }

  const availableCategorySet = new Set(
    availableCategories.map(normalizeProjectParameterCategoryKey)
  );
  return normalizedRequestedCategories.every((category) =>
    availableCategorySet.has(normalizeProjectParameterCategoryKey(category))
  );
}

function isAuthoritativeProjectParameter(
  parameter: AvailableProjectParameterDescriptor
): boolean {
  return parameter.isProjectBinding === true ||
    parameter.group?.trim().toLowerCase() === 'project information';
}

function descriptorMatchesDataType(
  parameterName: string,
  parameter: AvailableProjectParameterDescriptor,
  parameterTypeHints: ParameterTypeHint[]
): boolean {
  const equivalentNames = new Set(
    getEquivalentRevitParameterNames(parameterName).map((name) => name.trim().toLowerCase())
  );
  const expectedType = parameterTypeHints.find((hint) =>
    equivalentNames.has(hint.name.trim().toLowerCase())
  )?.dataType.trim().toUpperCase();
  const availableType = parameter.dataType?.trim().toUpperCase();
  return Boolean(expectedType && availableType === expectedType);
}

export function doesProjectParameterDescriptorCoverBindingAndCategories(
  parameter: AvailableProjectParameterDescriptor,
  bindingKind: ParameterBindingKind,
  requestedCategories: string[] | undefined
): boolean {
  return isAuthoritativeProjectParameter(parameter) &&
    parameter.parameterType === toProjectParameterType(bindingKind) &&
    descriptorCoversCategories(parameter, requestedCategories ?? []);
}

export function findAuthoritativeProjectParameterBindingKind(
  parameterName: string,
  availableParameters: AvailableProjectParameterDescriptor[],
  parameterTypeHints: ParameterTypeHint[] = []
): ParameterBindingKind | null {
  const equivalentNames = new Set(
    getEquivalentRevitParameterNames(parameterName).map((name) => name.trim().toLowerCase())
  );
  const authoritativeDescriptor = availableParameters.find((parameter) =>
    equivalentNames.has(parameter.name.trim().toLowerCase()) &&
    isAuthoritativeProjectParameter(parameter) &&
    descriptorMatchesDataType(parameterName, parameter, parameterTypeHints)
  );
  if (!authoritativeDescriptor) {
    return null;
  }

  return authoritativeDescriptor.parameterType === 'Type' ? 'type' : 'instance';
}

export function areProjectParametersAvailableForBindingAndCategories(
  requestedParameters: string[],
  bindingKind: ParameterBindingKind,
  requestedCategories: string[] | undefined,
  availableParameters: AvailableProjectParameterDescriptor[],
  parameterTypeHints: ParameterTypeHint[] = []
): boolean {
  return sanitizeRevitParameterNames(requestedParameters).every((parameterName) => {
    const equivalentNames = new Set(
      getEquivalentRevitParameterNames(parameterName).map((name) => name.trim().toLowerCase())
    );
    return availableParameters.some((parameter) =>
      equivalentNames.has(parameter.name.trim().toLowerCase()) &&
      doesProjectParameterDescriptorCoverBindingAndCategories(
        parameter,
        bindingKind,
        requestedCategories
      ) &&
      descriptorMatchesDataType(parameterName, parameter, parameterTypeHints)
    );
  });
}

export function buildTolerantProjectParameterMutationBatches(
  requestedParameters: string[],
  requestedBindingKind: ParameterBindingKind,
  requestedCategories: string[],
  availableParameters: AvailableProjectParameterDescriptor[],
  parameterTypeHints: ParameterTypeHint[] = []
): ProjectParameterMutationBatch[] {
  const batches = new Map<ParameterBindingKind, string[]>();

  sanitizeRevitParameterNames(requestedParameters).forEach((parameterName) => {
    const equivalentNames = new Set(
      getEquivalentRevitParameterNames(parameterName).map((name) => name.trim().toLowerCase())
    );
    const compatibleDescriptor = availableParameters.find((parameter) =>
      equivalentNames.has(parameter.name.trim().toLowerCase()) &&
      doesProjectParameterDescriptorCoverBindingAndCategories(
        parameter,
        requestedBindingKind,
        requestedCategories
      ) &&
      descriptorMatchesDataType(parameterName, parameter, parameterTypeHints)
    );
    if (compatibleDescriptor) {
      return;
    }

    const batch = batches.get(requestedBindingKind) ?? [];
    batch.push(parameterName);
    batches.set(requestedBindingKind, batch);
  });

  return Array.from(batches, ([bindingKind, parameterNames]) => ({
    bindingKind,
    parameterNames,
  }));
}

export function getNewProjectParameterNames(
  requestedParameters: string[],
  availableParameters: AvailableProjectParameterDescriptor[]
): string[] {
  const availableNames = new Set(
    availableParameters
      // Sampled element parameters do not prove that a project binding existed
      // before the mutation and therefore must not be excluded from undo.
      .filter(isAuthoritativeProjectParameter)
      .flatMap((parameter) =>
        getEquivalentRevitParameterNames(parameter.name).map((name) => name.trim().toLowerCase())
      )
  );
  return sanitizeRevitParameterNames(requestedParameters).filter((parameterName) =>
    getEquivalentRevitParameterNames(parameterName).every(
      (name) => !availableNames.has(name.trim().toLowerCase())
    )
  );
}
